import AuthApi from '../../apis/auth';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { CurrentUser } from '../../params/redux';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import { resetAllStates } from '../../helper/reset_helper';
import { RootState } from '../../redux/store';
import { EditProfileParams, LoginParams, LoginResponse, SignUpParams, SignUpResponse, UpdatePasswordParams } from '../../params/auth';
import { resettingAppData, resettingAppLoader } from '../slices';

interface UserResponseObject extends Record<string, unknown> {
  success: 0 | 1;
  msg?: string;
  user?: CurrentUser;
  agency_name?: string;
  billing_enabled?: boolean;
  plan_id?: number;
  plan_name?: string;
  subscription_status?: 'trialing' | 'active' | 'past_due';
}

export const fetchUserDetailThunk = createAsyncThunk(
  'app/fetch-user',
  async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
    try {
      const state = getState() as RootState;
      const response: UserResponseObject = await AuthApi.fetchUserDetail(state.app.token) as UserResponseObject;
      if (response?.success === 1) {
        return {
          user: response.user,
          billing_enabled: response.billing_enabled,
          plan_id: response.plan_id,
          plan_name: response.plan_name,
          subscription_status: response.subscription_status
        };
      }
      return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
    } catch (error) {
      if (isErrorWithMessage(error)) {
        return errorHandler(error.message, dispatch, rejectWithValue);
      }
      return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
    } finally {
      dispatch(resettingAppLoader());
    }
  }
);

export const loginThunk = createAsyncThunk(
  'app/login',
  async ({ email, password }: LoginParams, { dispatch, rejectWithValue }): Promise<LoginResponse | undefined | void> => {
    try {
      const userDetails = {
        email,
        password
      };
      const response: LoginResponse = await AuthApi.login(userDetails);
      if (response?.success === 1) {
        resetAllStates(dispatch);
        return {
          success: response.success,
          userId: response.userId,
          token: response.token,
          name: response.name,
          email: response.email,
          role: response.role,
          agency_name: response.agency_name,
          client_id: response.client_id || 0,
          plan_id: response.plan_id,
          plan_name: response.plan_name,
          billing_enabled: response.billing_enabled
        };
      }
      // dispatch(showError({ title: 'Error', description: response.msg }));
      return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
    } catch (error) {
      if (isErrorWithMessage(error)) {
        return errorHandler(error.message, dispatch, rejectWithValue);
      }
      return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
    } finally {
      dispatch(resettingAppLoader());
    }
  }
);

export const signupThunk = createAsyncThunk(
  'app/signup',
  async ({ first_name, last_name, organization_name, email, password, special_link }: SignUpParams, { dispatch, rejectWithValue }): Promise<SignUpResponse | undefined | void> => {
    try {
      const userDetails: SignUpParams = {
        first_name,
        last_name,
        organization_name,
        email,
        password,
        special_link
      };
      const response: SignUpResponse = await AuthApi.signup(userDetails);
      if (response?.success === 1) {
        resetAllStates(dispatch);
        return {
          success: response.success,
          userId: response.userId,
          token: response.token,
          name: response.name,
          email: response.email,
          role: response.role,
          agency_name: response.agency_name,
          client_id: response.client_id || 0,
          plan_id: response.plan_id,
          plan_name: response.plan_name,
          billing_enabled: response.billing_enabled,
          subscription_status: response.subscription_status
        };
      }
      // dispatch(showError({ title: 'Error', description: response.msg }));
      return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
    } catch (error) {
      if (isErrorWithMessage(error)) {
        return errorHandler(error.message, dispatch, rejectWithValue);
      }
      return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
    } finally {
      dispatch(resettingAppLoader());
    }
  }
);

export const logoutThunk = createAsyncThunk(
  'app/logout',
  async (_, { dispatch, rejectWithValue, getState }): Promise<undefined | void | string> => {
    try {
      const { app: { token }} = getState() as RootState;
      await AuthApi.logout(token);
      resetAllStates(dispatch);
      if (window.$crisp) {
        window.$crisp.push(['do', 'chat:hide']);
      }
      // const crispScript = document.querySelector('script[src="https://client.crisp.chat/l.js"]');
      // if (crispScript) {
      //   crispScript.remove();
      // }
      // delete (window as any).$crisp;
      // delete (window as any).CRISP_WEBSITE_ID;
      return '';
    } catch (error) {
      if (isErrorWithMessage(error)) {
        return errorHandler(error.message, dispatch, rejectWithValue);
      }
      return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
    } finally {
      dispatch(resettingAppData());
    }
  }
);

export const editProfileThunk = createAsyncThunk(
  'app/edit',
  async (data: EditProfileParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
    try {
      const state = getState() as RootState;
      const response = await AuthApi.editProfile(state.app.token, data);
      if (response?.success === 1) {
        await dispatch(fetchUserDetailThunk()).unwrap();
        return response;
      }
      return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
    } catch (error) {
      if (isErrorWithMessage(error)) {
        return errorHandler(error.message, dispatch, rejectWithValue);
      }
      return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
    } finally {
      dispatch(resettingAppLoader());
    }
  }
);

export const deactivateAccountThunk = createAsyncThunk(
  'app/deactivate',
  async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
    try {
      const state = getState() as RootState;
      const response = await AuthApi.deactivateAccount(state.app.token);
      if (response?.success === 1) {
        return response;
      }
      return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
    } catch (error) {
      if (isErrorWithMessage(error)) {
        return errorHandler(error.message, dispatch, rejectWithValue);
      }
      return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
    } finally {
      dispatch(resettingAppLoader());
    }
  }
);

export const activateAccountThunk = createAsyncThunk(
  'app/activate',
  async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
    try {
      const state = getState() as RootState;
      const response = await AuthApi.activateAccount(state.app.token);
      if (response?.success === 1) {
        return response;
      }
      return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
    } catch (error) {
      if (isErrorWithMessage(error)) {
        return errorHandler(error.message, dispatch, rejectWithValue);
      }
      return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
    } finally {
      dispatch(resettingAppLoader());
    }
  }
);

export const updatePasswordThunk = createAsyncThunk(
  'app/update-password',
  async (data: UpdatePasswordParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
    try {
      const state = getState() as RootState;
      const response = await AuthApi.updatePassword(state.app.token, data);
      if (response?.success === 1) {
        await dispatch(fetchUserDetailThunk()).unwrap();
        return response;
      }
      return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
    } catch (error) {
      if (isErrorWithMessage(error)) {
        return errorHandler(error.message, dispatch, rejectWithValue);
      }
      return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
    } finally {
      dispatch(resettingAppLoader());
    }
  }
);

export const sendEmailForResettingPasswordThunk = createAsyncThunk(
  'app/reset',
  async (email: string, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
    try {
      const state = getState() as RootState;
      const response = await AuthApi.sendResetPasswordEmail(state.app.token, email);
      if (response?.success === 1) {
        return response;
      }
      return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
    } catch (error) {
      if (isErrorWithMessage(error)) {
        return errorHandler(error.message, dispatch, rejectWithValue);
      }
      return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
    } finally {
      dispatch(resettingAppLoader());
    }
  }
);

export const resetPasswordThunk = createAsyncThunk(
  'app/reset-password',
  async ({ code, password }: { code: string, password: string }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
    try {
      const state = getState() as RootState;
      const response = await AuthApi.setResetPassword(state.app.token, code, password);
      if (response?.success === 1) {
        return response;
      }
      return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
    } catch (error) {
      if (isErrorWithMessage(error)) {
        return errorHandler(error.message, dispatch, rejectWithValue);
      }
      return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
    } finally {
      dispatch(resettingAppLoader());
    }
  }
);